<!-- Shares the full Salt playback page's pixel-space contours. The SVG is a
     view of the existing playback clock; only a completed gesture emits seek. -->
<template>
  <div class="salt-capsule-progress" :class="{ dragging, hovered, 'pointer-focus': pointerFocus, disabled: !canSeek }" data-salt-capsule-progress>
    <svg v-if="geometry" ref="svg" class="capsule-track" :viewBox="`0 0 ${geometry.width} ${geometry.height}`">
      <defs><mask :id="maskId" maskUnits="userSpaceOnUse" x="-4" y="-4" :width="geometry.width + 8" :height="geometry.height + 8"><path :d="geometry.progress" pathLength="1" fill="none" stroke="white" stroke-width="4" stroke-linecap="butt" :stroke-dasharray="ratio + ' 1'" data-salt-progress-mask /></mask></defs>
      <path class="capsule-outline" :d="geometry.outline" aria-hidden="true" />
      <path class="capsule-taper" :d="geometry.taper" data-salt-taper-track aria-hidden="true" />
      <path class="capsule-elapsed" :d="geometry.taper" :mask="`url(#${maskId})`" data-salt-elapsed aria-hidden="true" />
      <path ref="seekPath" class="capsule-seek" :d="geometry.progress" fill="none" stroke="transparent" stroke-width="16" role="slider" :tabindex="canSeek ? 0 : -1" aria-label="播放进度" aria-valuemin="0" :aria-valuemax="total" :aria-valuenow="shown" :aria-valuetext="timeText" :aria-disabled="String(!canSeek)" data-salt-capsule-seek
        @pointerdown.stop="onPointerDown" @pointermove="onPointerMove" @pointerup.stop="onPointerUp" @pointercancel="onPointerCancel" @lostpointercapture="onLostPointerCapture" @pointerenter="hovered = true" @pointerleave="hovered = false" @click.stop.prevent @keydown="onKeydown" @blur="pointerFocus = false" />
    </svg>
    <output class="capsule-time" aria-hidden="true">{{ timeText }}</output>
  </div>
</template>
<script>
import { saltTrackGeometry } from '../../../../vendor/amll-page/packages/react-full/src/components/PrebuiltLyricPlayer/salt-track';

let progressSequence = 0;
const formatTime = value => {
  const seconds = Math.floor(Math.max(0, Number(value) || 0));
  return Math.floor(seconds / 60) + ':' + String(seconds % 60).padStart(2, '0');
};

export default {
  name: 'SaltCapsuleProgress',
  props: { position: { type: Number, default: 0 }, duration: { type: Number, default: 0 }, remaining: Boolean, disabled: Boolean, trackId: { type: String, default: '' } },
  data() { return { geometry: null, maskId: 'salt-capsule-progress-' + ++progressSequence, preview: null, dragging: false, hovered: false, pointerFocus: false }; },
  computed: {
    total() { return Number.isFinite(this.duration) ? Math.max(0, this.duration) : 0; },
    canSeek() { return !this.disabled && this.total > 0; },
    shown() { return this.bound(this.preview === null ? this.position : this.preview); },
    ratio() { return this.total > 0 ? this.shown / this.total : 0; },
    timeText() { return formatTime(this.shown) + ' / ' + (this.remaining ? '-' + formatTime(this.total - this.shown) : formatTime(this.total)); },
  },
  watch: {
    position(value) { if (!this.dragging && this.preview !== null && Math.abs(value - this.preview) < 0.5) this.clearPreview(); },
    trackId() { this.cancelDrag(); },
    canSeek(value) { if (!value) this.cancelDrag(); },
    total() { if (this.preview !== null) this.preview = this.bound(this.preview); },
  },
  mounted() {
    this._resize = () => {
      const { width, height } = this.$el.getBoundingClientRect();
      if (width <= 0 || height <= 0) return;
      const radius = parseFloat(getComputedStyle(this.$el).borderTopLeftRadius) || height / 2;
      const next = saltTrackGeometry(width, height, radius);
      if (this.geometry && this.geometry.width === next.width && this.geometry.height === next.height && this.geometry.radius === next.radius) return;
      this.geometry = next;
      this._pathSamples = null;
    };
    this._observer = new ResizeObserver(this._resize);
    this._observer.observe(this.$el);
    this._resize();
  },
  beforeDestroy() { this.cancelDrag(); this._observer?.disconnect(); },
  methods: {
    bound(value) { return Math.min(this.total, Math.max(0, Number.isFinite(value) ? value : 0)); },
    clearPreview() { clearTimeout(this._resetTimer); this.preview = null; },
    cancelDrag() {
      const id = this._pointerId;
      this._pointerId = null;
      this.dragging = false;
      this.clearPreview();
      if (id !== null && id !== undefined && this.$refs.seekPath?.hasPointerCapture(id)) this.$refs.seekPath.releasePointerCapture(id);
    },
    pointerPosition(event) {
      const svg = this.$refs.svg, path = this.$refs.seekPath;
      const matrix = svg.getScreenCTM();
      if (!matrix) return this.shown;
      const screen = svg.createSVGPoint();
      screen.x = event.clientX; screen.y = event.clientY;
      const point = screen.matrixTransform(matrix.inverse());
      if (point.x <= 0.65) return 0;
      if (point.x >= this.geometry.width - 0.65) return this.total;
      // Equal arc-length samples are cached per geometry, not per clock tick.
      // Project onto those short segments so both curved tips map to the same
      // arc-length ratio used by the mask, including a corner's vertical part.
      const definition = path.getAttribute('d');
      if (!this._pathSamples || this._samplePathDefinition !== definition) {
        const length = path.getTotalLength();
        this._pathSamples = Array.from({ length: 129 }, (_, index) => path.getPointAtLength(length * index / 128));
        this._samplePathDefinition = definition;
      }
      const samples = this._pathSamples;
      let distance = Infinity, fraction = 0;
      for (let index = 1; index < samples.length; index++) {
        const a = samples[index - 1], b = samples[index];
        const dx = b.x - a.x, dy = b.y - a.y, lengthSquared = dx * dx + dy * dy;
        const along = lengthSquared ? Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared)) : 0;
        const candidate = (point.x - a.x - along * dx) ** 2 + (point.y - a.y - along * dy) ** 2;
        if (candidate < distance) { distance = candidate; fraction = (index - 1 + along) / (samples.length - 1); }
      }
      return this.bound(fraction * this.total);
    },
    onPointerDown(event) {
      if (!this.canSeek || event.button !== 0 || event.isPrimary === false || this.dragging) return;
      event.preventDefault(); clearTimeout(this._resetTimer);
      this.pointerFocus = true;
      event.currentTarget.focus({ preventScroll: true });
      event.currentTarget.setPointerCapture(event.pointerId);
      this._pointerId = event.pointerId;
      this.dragging = true;
      this.preview = this.pointerPosition(event);
    },
    onPointerMove(event) { if (this.dragging && event.pointerId === this._pointerId) this.preview = this.pointerPosition(event); },
    onPointerUp(event) {
      if (!this.dragging || event.pointerId !== this._pointerId) return;
      const value = this.pointerPosition(event);
      this._pointerId = null;
      this.dragging = false;
      this.commit(value);
      if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    },
    onPointerCancel(event) { if (event.pointerId === this._pointerId) this.cancelDrag(); },
    onLostPointerCapture(event) { if (event.pointerId === this._pointerId) this.cancelDrag(); },
    commit(value) {
      clearTimeout(this._resetTimer);
      this.preview = this.bound(value);
      this.$emit('seek', this.preview);
      this._resetTimer = setTimeout(() => { this.preview = null; }, 1000);
    },
    onKeydown(event) {
      if (!this.canSeek) return;
      const value = event.key === 'Home' ? 0 : event.key === 'End' ? this.total : event.key === 'ArrowRight' || event.key === 'ArrowUp' ? this.shown + 5 : event.key === 'ArrowLeft' || event.key === 'ArrowDown' ? this.shown - 5 : null;
      if (value === null) return;
      event.preventDefault(); event.stopPropagation();
      this.pointerFocus = false;
      this.commit(value);
    },
  },
};
</script>
<style scoped>
.salt-capsule-progress { position:absolute; inset:0; z-index:3; border-radius:inherit; pointer-events:none; }
.capsule-track { position:absolute; inset:0; width:100%; height:100%; pointer-events:none; overflow:visible; }
.capsule-outline { fill:none; stroke:var(--salt-capsule-outline,var(--salt-subtext,#8c8c8c)); stroke-opacity:.16; stroke-width:.7px; vector-effect:non-scaling-stroke; }
.capsule-taper { fill:var(--salt-capsule-rail,var(--salt-subtext,#8c8c8c)); fill-opacity:.24; stroke:none; }
.capsule-elapsed { fill:var(--salt-capsule-elapsed,var(--salt-text,#1e1715)); fill-opacity:.83; stroke:none; }
.capsule-seek { pointer-events:stroke; touch-action:none; cursor:default; outline:none; box-shadow:none; }
.capsule-seek:focus,.capsule-seek:focus-visible { outline:none; box-shadow:none; }
.disabled .capsule-seek { pointer-events:none; }
.capsule-time { position:absolute; bottom:calc(100% + 8px); left:50%; transform:translateX(-50%); padding:4px 8px; border:1px solid var(--salt-stroke,#8882); border-radius:4px; background:var(--salt-popup,#f9f9f9); color:var(--salt-text,#1e1715); font:400 12px/1.5 'Segoe UI','Microsoft YaHei',sans-serif; font-variant-numeric:tabular-nums; white-space:nowrap; opacity:0; pointer-events:none; }
.hovered .capsule-time,.dragging .capsule-time,.salt-capsule-progress:focus-within:not(.pointer-focus) .capsule-time { opacity:1; }
.salt-capsule-progress:focus-within:not(.pointer-focus) .capsule-time { text-decoration:underline; text-decoration-thickness:2px; text-underline-offset:3px; }
</style>
